import { databases, DATABASE_ID, COLLECTION_ID_TASK_MANAGEMENT } from '../config/appwrite';
import { ID, Query } from 'appwrite';

/**
 * Task Management Service
 * Handles all database operations for Task Management
 * Collection Name: "Task Management SGM"
 * Collection ID: "TaskManagementSGM" (Appwrite doesn't allow spaces in collection IDs)
 */

// Get all tasks
export const getAllTasks = async () => {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_TASK_MANAGEMENT,
      [
        Query.orderDesc('$createdAt'),
        Query.limit(1000)
      ]
    );
    return response.documents.map(formatTask);
  } catch (error) {
    console.error('Error fetching tasks:', error);
    // If collection doesn't exist, return empty array instead of throwing
    if (error.code === 404 || error.message?.includes('not found') || error.message?.includes('Collection')) {
      console.warn('Task Management collection not found. Please run: npm run setup:task-management');
      return [];
    }
    throw error;
  }
};

// Get a single task by ID
export const getTaskById = async (id) => {
  try {
    const response = await databases.getDocument(
      DATABASE_ID,
      COLLECTION_ID_TASK_MANAGEMENT,
      id
    );
    return formatTask(response);
  } catch (error) {
    console.error('Error fetching task:', error);
    throw error;
  }
};

// Create a new task
export const createTask = async (taskData) => {
  try {
    const documentData = {
      title: taskData.title || '',
      assignee: taskData.assignee || '',
      dueDate: taskData.dueDate || '',
      status: taskData.status || 'TODO',
      priority: taskData.priority || 'Medium Priority',
      category: taskData.category || 'Admin',
      description: taskData.description || '',
      createdDate: taskData.createdDate || new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
      currentStatus: taskData.currentStatus || 'To Do'
    };

    const response = await databases.createDocument(
      DATABASE_ID,
      COLLECTION_ID_TASK_MANAGEMENT,
      ID.unique(),
      documentData
    );
    return formatTask(response);
  } catch (error) {
    console.error('Error creating task:', error);
    throw error;
  }
};

// Update a task
export const updateTask = async (id, taskData) => {
  try {
    const documentData = {};
    
    if (taskData.title !== undefined) documentData.title = taskData.title;
    if (taskData.assignee !== undefined) documentData.assignee = taskData.assignee;
    if (taskData.dueDate !== undefined) documentData.dueDate = taskData.dueDate;
    if (taskData.status !== undefined) documentData.status = taskData.status;
    if (taskData.priority !== undefined) documentData.priority = taskData.priority;
    if (taskData.category !== undefined) documentData.category = taskData.category;
    if (taskData.description !== undefined) documentData.description = taskData.description;
    if (taskData.createdDate !== undefined) documentData.createdDate = taskData.createdDate;
    if (taskData.currentStatus !== undefined) documentData.currentStatus = taskData.currentStatus;

    const response = await databases.updateDocument(
      DATABASE_ID,
      COLLECTION_ID_TASK_MANAGEMENT,
      id,
      documentData
    );
    return formatTask(response);
  } catch (error) {
    console.error('Error updating task:', error);
    throw error;
  }
};

// Delete a task
export const deleteTask = async (id) => {
  try {
    await databases.deleteDocument(
      DATABASE_ID,
      COLLECTION_ID_TASK_MANAGEMENT,
      id
    );
    return { success: true };
  } catch (error) {
    console.error('Error deleting task:', error);
    throw error;
  }
};

// Format task from Appwrite document format to app format
const formatTask = (doc) => {
  return {
    id: doc.$id,
    documentId: doc.$id,
    title: doc.title || '',
    assignee: doc.assignee || '',
    dueDate: doc.dueDate || '',
    status: doc.status || 'TODO',
    priority: doc.priority || 'Medium Priority',
    category: doc.category || 'Admin',
    description: doc.description || '',
    createdDate: doc.createdDate || '',
    currentStatus: doc.currentStatus || 'To Do'
  };
};
